import React, { Suspense, Fragment, lazy } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import DashboardLayout from 'src/layouts/DashboardLayout';
import MainLayout from 'src/layouts/MainLayout';
import AuthGuard from 'src/guards/AuthGuard';
import GuestGuard from 'src/guards/GuestGuard';
import LoadingScreen from 'src/components/LoadingScreen';

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;
        const redirectPath = route.redirect;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={props => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : redirectPath ? (
                    <Redirect to="/app/dashboard" />
                  ) : (
                    <Component {...props} />
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes = [
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app/account',
        component: lazy(() => import('src/views/account/AccountView'))
      },
      {
        exact: true,
        path: '/app/customers',
        component: lazy(() => import('src/views/customer/CustomerListView'))
      },
      {
        exact: true,
        path: '/app/payers',
        component: lazy(() => import('src/views/payers'))
      },
      {
        exact: true,
        path: '/app/management/customers/:customerId',
        component: lazy(() => import('src/views/customer/CustomerView'))
      },
      {
        exact: true,
        path: '/app/management/customers/:customerId/edit',
        component: lazy(() => import('src/views/customer/CustomerUpdateView'))
      },
      {
        exact: true,
        path: '/app/management/invoices',
        component: lazy(() => import('src/views/invoices/InvoiceList'))
      },
      {
        exact: true,
        path: '/app/management/logs',
        component: lazy(() => import('src/views/logs/LogsList'))
      },
      {
        exact: true,
        path: '/app/management/bin',
        component: lazy(() => import('src/views/bin'))
      },
      {
        exact: true,
        path: '/app/management/invoices/:invoiceId',
        component: lazy(() => import('src/views/invoices/InvoiceDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/account',
        component: lazy(() => import('src/views/account/Management/ListView'))
      },
      {
        exact: true,
        path: '/app/management/account/:adminId/edit',
        component: lazy(() => import('src/views/account/Management/UpdateView'))
      },
      {
        exact: true,
        path: '/app/management/account/:adminId/create',
        component: lazy(() => import('src/views/account/Management/CreateView'))
      },
      {
        exact: true,
        path: '/app/management/account/create',
        component: lazy(() => import('src/views/account/Management/CreateView'))
      },
      {
        exact: true,
        path: '/app/teachers',
        component: lazy(() => import('src/views/teacher/TeacherList'))
      },
      {
        exact: true,
        path: '/app/management/teacher/create',
        component: lazy(() => import('src/views/teacher/TeacherCreateView'))
      },
      {
        exact: true,
        path: '/app/management/teacher/:teacherId/edit',
        component: lazy(() => import('src/views/teacher/TeacherCreateView'))
      },
      {
        exact: true,
        path: '/app/management/teacher/:teacherId',
        component: lazy(() => import('src/views/teacher/TeacherView'))
      },
      {
        exact: true,
        path: '/app/courses',
        component: lazy(() => import('src/views/courses/CourseTableView'))
      },
      {
        exact: true,
        path: '/app/live-streams',
        component: lazy(() => import('src/views/streams/StreamsTableView'))
      },
      {
        exact: true,
        path: '/app/management/live-streams/:lessonId',
        component: lazy(() => import('src/views/streams/StreamView'))
      },
      {
        exact: true,
        path: '/app/management/course/create',
        component: lazy(() => import('src/views/courses/CourseCreateView'))
      },
      {
        exact: true,
        path: '/app/management/course/edit',
        component: lazy(() => import('src/views/courses/CourseCreateView'))
      },
      {
        exact: true,
        path: '/app/subjects',
        component: lazy(() => import('src/views/subjects/SubjectListView'))
      },
      {
        exact: true,
        path: '/app/management/subjects/create',
        component: lazy(() => import('src/views/subjects/SubjectCreateView'))
      },
      {
        exact: true,
        path: '/app/dashboard',
        component: lazy(() => import('src/views/reports/DashboardView'))
      },
      {
        exact: true,
        path: '/app/settings',
        component: lazy(() => import('src/views/settings/SettingsView'))
      },
      {
        exact: true,
        path: '/app/exam-center',
        component: lazy(() => import('src/views/exams/ExamCenterView'))
      },
      {
        exact: true,
        path: '/app/courier/reports',
        component: lazy(() => import('src/views/courier/reports/ReportCreateView'))
      },
      {
        exact: true,
        path: '/app/courier/labels',
        component: lazy(() => import('src/views/courier/labels/LabelCreateView'))
      }
    ]
  },
  {
    path: '/auth',
    layout: MainLayout,
    routes: [
      {
        exact: true,
        guard: GuestGuard,
        path: '/auth/login',
        component: lazy(() => import('src/views/auth/LoginView'))
      }
    ]
  },
  {
    path: '',
    layout: MainLayout,
    routes: [
      {
        exact: true,
        path: '',
        redirect: '/app/dashboard'
      },
      {
        component: lazy(() => import('src/views/errors/NotFoundView'))
      }
    ]
  }
];

export default routes;
